import { useEffect, useState } from 'react';
import { Badge, Button, Caption1, MessageBar, MessageBarBody, Spinner, Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PopupDialog, shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import { AGENCY_TIME_ZONE, personDisplayName, type Person, type PortalLinkRow } from '@vigeo/shared';
import { PortalLinkDialog, usePortalLinkHandOver } from '@/features/people';
import { fetchPeople, fetchPortalLinks, revokePortalLink } from '@/lib/api';
import { fetchAuthState } from '@/lib/auth';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  head: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  /** Page-name voice (24/700), as on Offers, People and Review. */
  title: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightBold },
  table: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  row: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalL, minWidth: 0,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  /** Offers' header row: the same flex bases as a row, hidden below tablet where rows stack and label themselves. */
  headerRow: {
    backgroundColor: tokens.colorNeutralBackground2,
    paddingBlock: tokens.spacingVerticalS,
    borderTop: 'none',
    [shellMedia.belowTablet]: { display: 'none' },
  },
  headerCell: { fontWeight: tokens.fontWeightSemibold },
  person: {
    display: 'flex', flexDirection: 'column', minWidth: '13rem', flexBasis: '16rem', flexGrow: 1,
    // The name is a link; on a phone it gets the 44px target Offers gives its own (#331).
    [shellMedia.phone]: { '& a': { display: 'inline-flex', alignItems: 'center', minHeight: shellMetrics.touchTarget } },
  },
  state: { display: 'flex', minWidth: '8rem', flexBasis: '9rem' },
  when: { display: 'flex', flexDirection: 'column', minWidth: '11rem', flexBasis: '14rem', flexGrow: 1 },
  // A 44px target on a phone (the touch-target gate), and never below the control height.
  actions: {
    display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: tokens.spacingHorizontalS,
    flexBasis: '14rem', flexGrow: 1, minHeight: '2.75rem', alignItems: 'center',
  },
});

/** The badge per state. The four the per-person panel draws use its words (`PortalStep`'s STATE_WORDS). */
const STATE_WORDS: Record<PortalLinkRow['state'], { readonly label: string; readonly tone: 'informative' | 'success' | 'warning' | 'danger' }> = {
  none: { label: 'No link', tone: 'informative' },
  active: { label: 'Active', tone: 'success' },
  expired: { label: 'Expired', tone: 'warning' },
  revoked: { label: 'Revoked', tone: 'danger' },
  unreadable: { label: 'Could not be read', tone: 'warning' },
};

const PURPOSE_WORDS: Record<PortalLinkRow['purpose'], string> = {
  'portal-session': 'Onboarding portal',
  'doc-request': 'Document request',
};

/** A day with its year, in the agency's zone, as every other HR date is drawn. */
function day(instant: string, locale = 'en-US'): string {
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric', timeZone: AGENCY_TIME_ZONE }).format(new Date(instant));
}

/**
 * Portal links (#802, frame 06b's Hiring menu): where every person's live portal link stands, from the
 * bounded cross-person read #303 added (`GET /api/portal-links`).
 *
 * THE POINTER DECIDES WHAT IS LIVE, and that is the server's job: a reissue leaves the old token
 * unrevoked, so only the person's active-token pointer says which link counts (#303, 2026-10-07).
 * Each row here is one pointer, as the API sent it. A person who was never issued a link has no
 * pointer and so no row.
 *
 * NO LINK AND NO CODE ON THE LIST. The read carries five facts and no secret (`portalLinkRowSchema`
 * is `.strict()`), so there is nothing here to show. The one place a link and code appear is the
 * shared hand-over dialog (#501), the same one the candidate's record opens: shown once, behind a
 * question, gone with "Done, hide them".
 *
 * THE NAME COMES FROM THE PEOPLE LIST, not from this read. The row carries only the person's id, on
 * purpose (`portalLinkRowSchema`'s comment), and the page joins it to `GET /api/people`, which is
 * itself bounded and shares its cache with Candidates and the Roster. A person the list did not
 * return still gets a row, named as such, with no action: the hand-over dialog needs their name.
 *
 * ACTIONS ARE ADMIN ONLY AND FOR THE ONBOARDING PORTAL ONLY. `portal-link.issue` and
 * `portal-link.revoke` are Admin (`auth/policy.ts`), and the server enforces both; the buttons are
 * hidden from a Viewer so they never meet a 403. The per-person routes act on the `portal-session`
 * link only, so a document-request row is shown and not acted on.
 */
export function PortalLinksPage() {
  const styles = useStyles();
  const links = useQuery({ queryKey: ['portal-links'], queryFn: fetchPortalLinks });
  // The same key Candidates, the Dashboard and the Roster read, so this costs no extra request there.
  const people = useQuery({ queryKey: ['people'], queryFn: () => fetchPeople() });
  // The same gate every other staff screen reads; a Viewer reads this list and acts on nothing.
  const auth = useQuery({ queryKey: ['auth'], queryFn: fetchAuthState, retry: false });
  const canEdit = auth.data?.status === 'signed-in' && auth.data.user.isAdmin;

  const byId = new Map((people.data ?? []).map((person) => [person.id, person]));

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>Portal links</h1>
        <Caption1>Where each person’s portal link stands. A link and its code are shown only once, when the link is made.</Caption1>
      </header>

      {links.isPending ? <Spinner size="small" label="Loading portal links" /> : null}

      {links.isError ? (
        <MessageBar intent="error">
          <MessageBarBody>Portal links could not be loaded. Try again shortly.</MessageBarBody>
        </MessageBar>
      ) : null}

      {people.isError ? (
        <MessageBar intent="warning">
          <MessageBarBody>Names could not be loaded, so each link is shown without one.</MessageBarBody>
        </MessageBar>
      ) : null}

      {links.data === undefined ? null : (
        <>
          {/* The bound is the API's and arrives on the response (#300); the screen never assumes it. */}
          {links.data.truncated ? (
            <MessageBar intent="warning">
              <MessageBarBody>{`Showing the first ${links.data.counted} links. There are more.`}</MessageBarBody>
            </MessageBar>
          ) : null}

          {links.data.rows.length === 0 ? (
            <MessageBar intent="info">
              <MessageBarBody>Nobody has been sent a portal link yet. Links are made from a candidate’s record.</MessageBarBody>
            </MessageBar>
          ) : (
            <div className={styles.table} role="list" aria-label="Portal links">
              <div className={mergeClasses(styles.row, styles.headerRow)} aria-hidden="true">
                <Text className={mergeClasses(styles.person, styles.headerCell)}>Person</Text>
                <Text className={mergeClasses(styles.state, styles.headerCell)}>Link</Text>
                <Text className={mergeClasses(styles.when, styles.headerCell)}>Dates</Text>
                <Text className={mergeClasses(styles.actions, styles.headerCell)}>{canEdit ? 'Actions' : ''}</Text>
              </div>
              {links.data.rows.map((row) => (
                <PortalLinkListRow
                  key={`${row.personId}:${row.purpose}`}
                  row={row}
                  person={byId.get(row.personId) ?? null}
                  namesPending={people.isPending}
                  canEdit={canEdit}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface RowProps {
  readonly row: PortalLinkRow;
  readonly person: Person | null;
  readonly namesPending: boolean;
  readonly canEdit: boolean;
}

function PortalLinkListRow({ row, person, namesPending, canEdit }: RowProps) {
  const styles = useStyles();
  const name = person === null ? (namesPending ? 'Loading name…' : 'Name not available') : personDisplayName(person.name);
  const words = STATE_WORDS[row.state];
  // Only the onboarding link has per-person issue and revoke routes, and the dialog needs a name.
  const actionable = canEdit && person !== null && row.purpose === 'portal-session';
  return (
    <div className={styles.row} role="listitem">
      <div className={styles.person}>
        <Link to={`/app/people/${encodeURIComponent(row.personId)}`}>{name}</Link>
        <Caption1>{PURPOSE_WORDS[row.purpose]}</Caption1>
      </div>
      <div className={styles.state}>
        <Badge appearance="filled" color={words.tone}>{words.label}</Badge>
      </div>
      <div className={styles.when}>
        <LinkDates row={row} />
      </div>
      <div className={styles.actions}>
        {actionable && person !== null ? <PortalLinkActions row={row} person={person} /> : null}
      </div>
    </div>
  );
}

/** The dates under the state, as the per-person panel words them. Absent dates are absent, never zeroed. */
function LinkDates({ row }: { readonly row: PortalLinkRow }) {
  if (row.state === 'none') return <Text>No live link.</Text>;
  if (row.state === 'unreadable') return <Text>This link’s record could not be read.</Text>;
  return <>
    {row.issuedAt === null ? null : <Text>{`Issued ${day(row.issuedAt)}`}</Text>}
    {row.state === 'revoked' && row.revokedAt !== null ? <Caption1>{`Revoked ${day(row.revokedAt)}`}</Caption1>
      : row.expiresAt === null ? null
        : <Caption1>{row.state === 'expired' ? `Expired ${day(row.expiresAt)}` : `Expires ${day(row.expiresAt)}`}</Caption1>}
    <Caption1>{row.firstUsedAt === null ? 'Not opened yet' : `First opened ${day(row.firstUsedAt)}`}</Caption1>
  </>;
}

/**
 * Revoke and Make a new link, for one person's onboarding link. The same two moves, words and questions
 * as the candidate's record (`PortalStep`), on the same routes, so the server writes the same audit rows.
 */
function PortalLinkActions({ row, person }: { readonly row: PortalLinkRow; readonly person: Person }) {
  const client = useQueryClient();
  const first = person.name.preferredName || person.name.givenName;
  /*
   * Both moves change the link, so both end by re-reading it: this list, and the candidate's own
   * panel if it is cached. The server's answer is the only thing that knows what the link is now.
   */
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['portal-links'] });
    void client.invalidateQueries({ queryKey: ['portal-link', person.id] });
  };
  const revoke = useMutation({ mutationFn: () => revokePortalLink(person.id), onSettled: refresh });
  const [askingRevoke, setAskingRevoke] = useState(false);
  const handOver = usePortalLinkHandOver(person, true);

  // A new link replaces the live one, so the row is stale the moment one is made. Keyed on the token, so
  // hiding the reveal does not re-read; the token itself goes nowhere but the shared dialog.
  const issuedToken = handOver.issued?.token ?? null;
  useEffect(() => {
    if (issuedToken !== null) refresh();
    // `refresh` closes over the query client, which is stable for the life of the app.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuedToken]);

  const busy = revoke.isPending || handOver.pending;
  return <>
    {row.state === 'active' ? (
      <Button disabled={busy} onClick={() => setAskingRevoke(true)}>Revoke link</Button>
    ) : null}
    <Button appearance="subtle" disabled={busy || handOver.issued !== null || handOver.asking} onClick={handOver.start}>
      {handOver.pending ? 'Making the link…' : 'Make a new link'}
    </Button>

    {revoke.data?.revoked === false || revoke.isError ? (
      <div>
        {revoke.data?.revoked === false ? <MessageBar intent="info"><MessageBarBody>There was no live link to revoke.</MessageBarBody></MessageBar> : null}
        {revoke.isError ? <MessageBar intent="error"><MessageBarBody>{revoke.error.message}</MessageBarBody></MessageBar> : null}
      </div>
    ) : null}

    <PortalLinkDialog handOver={handOver} firstName={first} />

    <PopupDialog
      open={askingRevoke}
      title={`Revoke ${first}'s link?`}
      startOn="cancel"
      busy={revoke.isPending}
      onClose={() => setAskingRevoke(false)}
      primary={{ label: 'Revoke the link', busyLabel: 'Revoking…', onClick: () => { setAskingRevoke(false); revoke.mutate(); } }}
    >
      <Text>{`${first} will not be able to sign in with the link or the access code they have. Anything already submitted is kept. To let ${first} back in you will have to create a new link.`}</Text>
    </PopupDialog>
  </>;
}
